// 伊索寓言：📖 故事 | 🎧 音频 两个大标签，页内切换，不整页跳转。
import type { Manifest } from "../../shared/manifest";
import { el, loadManifest, mediaUrl, routeLink, topbar } from "../lib";
import { PATHS, playerUrl } from "../../shared/navigation";

function renderStories(container: HTMLElement, manifest: Manifest): void {
  const intro = el("p", "hint", `共 ${manifest.stories.length} 篇 · 点一张卡片开始看故事`);
  container.appendChild(intro);

  if (manifest.stories.length === 0) {
    container.appendChild(
      el("p", "hint", "还没有故事媒体。请先运行 npm run prepare && npm run upload（见 README）。")
    );
    return;
  }

  const grid = el("div", "grid");
  for (const story of manifest.stories) {
    const card = routeLink(playerUrl(story.id), "card");
    card.setAttribute("aria-label", `播放第 ${story.id} 篇 ${story.title}`);

    const num = story.id.replace(/^0+/, "") || "0";
    const badge = el("span", "badge", num);
    const hue = (parseInt(num, 10) * 37 + 8) % 360;
    badge.style.setProperty("--hue", String(hue));

    card.appendChild(badge);
    card.appendChild(el("span", "t", `${story.id} ${story.title}`));
    card.appendChild(el("span", "go", "▶ 看故事"));
    grid.appendChild(card);
  }
  container.appendChild(grid);
}

/** 拉一个预签名直连 URL(仅 SW 未接管或签名过期重试时用)。 */
async function directAudioUrl(key: string): Promise<string | null> {
  try {
    const res = await fetch(`/api/sign?k=${encodeURIComponent(key)}`, {
      headers: { accept: "application/json" },
    });
    if (!res.ok) return null;
    const { url } = (await res.json()) as { url?: string };
    return url ?? null;
  } catch {
    return null;
  }
}

function renderAudioList(container: HTMLElement, manifest: Manifest): void {
  container.appendChild(el("p", "hint", `共 ${manifest.audio.length} 首 · 点 ▶ 听一听`));
  if (manifest.audio.length === 0) {
    container.appendChild(el("p", "hint", "暂无音频文件（存储 audio/ 为空）。"));
    return;
  }

  const list = el("ul", "audioList");
  list.id = "audioList";
  for (const item of manifest.audio) {
    const key = `audio/${item.file}`;
    const li = el("li");
    li.appendChild(el("span", "label", item.label || item.file));
    const audio = document.createElement("audio");
    audio.controls = true;
    audio.preload = "none";
    // 音频永远直接用预签名 URL(src 字面即七牛地址,不经 Worker/SW);
    // 签名失败兜底 /media,播放时 SW 仍会直连,过期自动重签。
    void directAudioUrl(key).then((url) => {
      audio.src = url ?? mediaUrl(key);
    });
    audio.addEventListener("error", () => {
      if (audio.dataset.retried) return;
      audio.dataset.retried = "1";
      void directAudioUrl(key).then((url) => {
        if (url) {
          audio.src = url;
          audio.load();
        }
      });
    });
    li.appendChild(audio);
    list.appendChild(li);
  }
  container.appendChild(list);
}

export async function aesopView(root: HTMLElement): Promise<void> {
  document.title = "伊索寓言 · 小朋友学习乐园";
  root.appendChild(
    topbar([
      { label: "英语", href: PATHS.english },
      { label: "伊索寓言" },
    ])
  );

  const main = el("main", "wrap main-pad");

  // 两个大标签
  const tabs = el("div", "tabs");
  const tabStories = el("button", "tab is-on") as HTMLButtonElement;
  tabStories.type = "button";
  tabStories.appendChild(el("span", "tab-emoji", "📖"));
  tabStories.appendChild(el("span", "tab-name", "故事"));
  const tabAudio = el("button", "tab") as HTMLButtonElement;
  tabAudio.type = "button";
  tabAudio.appendChild(el("span", "tab-emoji", "🎧"));
  tabAudio.appendChild(el("span", "tab-name", "音频"));
  tabs.appendChild(tabStories);
  tabs.appendChild(tabAudio);
  main.appendChild(tabs);

  const panel = el("div", "tab-panel");
  main.appendChild(panel);
  root.appendChild(main);

  let manifest: Manifest;
  try {
    manifest = await loadManifest();
  } catch (err) {
    panel.appendChild(
      el("p", "hint", "清单加载失败：" + ((err as Error).message ?? String(err)))
    );
    return;
  }

  if (!panel.isConnected) return;

  let current: "stories" | "audio" = "stories";
  const show = (which: "stories" | "audio"): void => {
    current = which;
    tabStories.classList.toggle("is-on", which === "stories");
    tabAudio.classList.toggle("is-on", which === "audio");
    panel.replaceChildren();
    if (which === "stories") renderStories(panel, manifest);
    else renderAudioList(panel, manifest);
  };
  tabStories.addEventListener("click", () => show("stories"));
  tabAudio.addEventListener("click", () => show("audio"));
  show(current);
}
